import React, { useState, useEffect, useMemo, useRef } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { 
  Settings, Check, X, Plus, Edit2, Save, Eye, EyeOff, Trash2,
  Package, Zap, Crown, AlertCircle, Loader2, DollarSign, Globe, Shield,
  ChevronUp, ChevronDown, GripVertical, Building2, Clock, FileUp, FileDown,
  Copy, Link2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import CustomPlansTab from './CustomPlansTab';
import TrialSettingsTab from './TrialSettingsTab';

// Tax contexts for preview
const TAX_CONTEXTS = {
  india: { name: 'India', gst: 18, label: 'GST @18%' },
  international: { name: 'International', gst: 0, label: 'Zero-Rated (Export)' }
};

// Default feature rows configuration - maps to database fields
const DEFAULT_PLAN_FEATURES = [
  { key: 'max_listings', label: 'No. of Listings', type: 'number', editable: true },
  { key: 'commission_percentage', label: 'Commission', type: 'percentage', editable: true },
  { key: 'listing_types_included', label: 'No. of Listing Types', type: 'number', editable: true },
  { key: 'product_addons_enabled', label: 'Variants / Product Options (Add-Ons)', type: 'boolean', editable: true },
  { key: 'media_storage_mb', label: 'Media Storage (MB)', type: 'number', editable: true },
  { key: 'master_catalogue_access', label: 'Master Catalogues Access', type: 'select', options: ['none', 'read_only', 'full'], editable: true },
  { key: 'ad_credits', label: 'Ad Credits', type: 'number', editable: true },
  { key: 'events_ticketing_enabled', label: 'Events Ticket with QR & Reader App', type: 'boolean', editable: true },
  { key: 'team_members_included', label: 'No. of Team Members', type: 'number', editable: true },
  { key: 'max_locations', label: 'No. of Locations', type: 'number', editable: true },
  { key: 'gst_billing_enabled', label: 'GST Billing', type: 'boolean', editable: true },
  { key: 'zoho_invoice_enabled', label: 'Zoho Invoice', type: 'boolean', editable: true },
  { key: 'whatsapp_transactional', label: 'WhatsApp Transactional Msgs', type: 'select', options: ['none', 'included', 'add_on'], editable: true },
  { key: 'international_markets_enabled', label: 'International Markets', type: 'boolean', editable: true },
  { key: 'reviews_ratings_enabled', label: 'Reviews & Ratings', type: 'boolean', editable: true },
  { key: 'support_level', label: 'Support Type', type: 'multiselect', options: ['email', 'chat', 'call', 'priority', 'dedicated'], editable: true },
  { key: 'lead_base_price', label: 'Lead Base Price', type: 'currency', editable: true },
];

// Add-ons configuration
const ADD_ONS_LIST = [
  { key: 'catalogue_pull', name: 'Catalogue Pull from Existing Store', defaultPrice: 1000, frequency: 'one-time' },
  { key: 'catalogue_sync', name: 'Catalogue Sync', defaultPrice: 500, frequency: '/mo' },
  { key: 'inventory_sync', name: 'Inventory Sync Real Time', defaultPrice: 500, frequency: '/mo' },
  { key: 'fulfillment_sync', name: 'Vendor Fulfillment Sync', defaultPrice: 500, frequency: '/mo' },
  { key: 'listing_setup_support', name: 'Listing Setup Support', defaultPrice: 2000, frequency: 'one-time' },
  { key: 'sms_marketing', name: 'SMS Marketing', defaultPrice: 1000, frequency: '/mo' },
  { key: 'email_marketing', name: 'Email Marketing', defaultPrice: 1500, frequency: '/mo' },
  { key: 'whatsapp_promotional', name: 'WhatsApp Automation - Promotional', defaultPrice: 2000, frequency: '/mo' },
  { key: 'whatsapp_crm', name: 'WhatsApp CRM with Own Number', defaultPrice: 2999, frequency: '/mo' },
  { key: 'extra_listing_type', name: 'Add Listing Type (Beyond Package)', defaultPrice: 500, frequency: '/mo' },
  { key: 'extra_team_member', name: 'Additional Team Member', defaultPrice: 500, frequency: '/mo' },
  { key: 'extra_location', name: 'Additional Location', defaultPrice: 500, frequency: '/mo' },
  { key: 'photography_services', name: 'Photography Services', defaultPrice: 5000, frequency: 'per-session' },
];

const SubscriptionPlansManager = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [plans, setPlans] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('plans');
  const [addOnsEditMode, setAddOnsEditMode] = useState(false);
  const [addOnsData, setAddOnsData] = useState([]);
  const [savingAddOns, setSavingAddOns] = useState(false);
  const [copiedPlanId, setCopiedPlanId] = useState(null);

  const handleCopyRazorpayPlanId = (planId, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopiedPlanId(planId);
      setTimeout(() => setCopiedPlanId(null), 1500);
    }).catch(() => {
      toast({ title: 'Copy failed', description: 'Could not access clipboard. Select and copy the ID manually.', variant: 'destructive' });
    });
  };
  
  // Tax context toggle (India vs International preview)
  const [taxContext, setTaxContext] = useState('india');
  
  // CRUD Modals State
  const [showAddPlanModal, setShowAddPlanModal] = useState(false);
  const [showDeletePlanModal, setShowDeletePlanModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [newPlanData, setNewPlanData] = useState({
    plan_name: '',
    plan_code: '',
    description: '',
    monthly_price: 0,
    annual_price: 0,
    commission_percentage: 15,
    max_listings: 10,
    media_storage_mb: 500,
    team_members_included: 1,
    max_locations: 1,
    trial_period_days: 14,
    support_level: 'email',
    display_order: 0
  });
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(false);
  
  // Add-on CRUD State
  const [showAddAddonModal, setShowAddAddonModal] = useState(false);
  const [showDeleteAddonModal, setShowDeleteAddonModal] = useState(false);
  const [addonToDelete, setAddonToDelete] = useState(null);
  const [newAddonData, setNewAddonData] = useState({
    name: '',
    key: '',
    price: 0,
    frequency: '/mo',
    is_active: true,
    linked_feature: ''  // Links to core feature for tier limitation
  });
  
  const [creatingAddon, setCreatingAddon] = useState(false);
  const [deletingAddon, setDeletingAddon] = useState(false);
  
  // Import/Export state
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Reordering state
  const [reordering, setReordering] = useState(false);
  
  // Feature list expansion state - show first 6 features by default
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const INITIAL_FEATURES_COUNT = 6;
  
  // Feature management state
  const [planFeatures, setPlanFeatures] = useState(DEFAULT_PLAN_FEATURES);
  const [showAddFeatureModal, setShowAddFeatureModal] = useState(false);
  const [showEditFeatureModal, setShowEditFeatureModal] = useState(false);
  const [showDeleteFeatureModal, setShowDeleteFeatureModal] = useState(false);
  const [featureToEdit, setFeatureToEdit] = useState(null);
  const [featureToDelete, setFeatureToDelete] = useState(null);
  const [newFeatureData, setNewFeatureData] = useState({
    key: '',
    label: '',
    type: 'boolean',
    options: []
  });
  
  // Generate linkable features from planFeatures dynamically for add-ons
  const LINKABLE_FEATURES = useMemo(() => [
    { key: '', label: 'None (Standalone Add-on)' },
    ...planFeatures.map(feature => ({
      key: feature.key,
      label: feature.label
    }))
  ], [planFeatures]);
  
  // Calculate price with GST based on selected context
  const getPriceWithGST = (basePrice) => {
    if (basePrice <= 0) return basePrice;
    const gst = TAX_CONTEXTS[taxContext].gst;
    return Math.round(basePrice * (1 + gst / 100));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const plansData = await apiCall('/api/subscription-plans/');
      const sortedPlans = plansData.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setPlans(sortedPlans);
      
      // Initialize edit data
      const initialEditData = {};
      sortedPlans.forEach(plan => {
        initialEditData[plan.id] = { ...plan };
      });
      setEditData(initialEditData);

      // Fetch feature definitions from backend
      try {
        const featureDefsRes = await apiCall('/api/subscription-plans/feature-definitions');
        if (featureDefsRes && featureDefsRes.length > 0) {
          setPlanFeatures(featureDefsRes);
        }
        // If no features in DB, keep DEFAULT_PLAN_FEATURES
      } catch (err) {
      }

      // Fetch or initialize add-ons
      try {
        const addOnsRes = await apiCall('/api/subscription-plans/addons');
        setAddOns(addOnsRes);
        setAddOnsData(addOnsRes);
      } catch {
        // Initialize with defaults if no add-ons exist
        const defaultAddOns = ADD_ONS_LIST.map(addon => ({
          id: addon.key,
          key: addon.key,
          name: addon.name,
          price: addon.defaultPrice,
          frequency: addon.frequency,
          is_active: true
        }));
        setAddOns(defaultAddOns);
        setAddOnsData(defaultAddOns);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load subscription plans',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== IMPORT/EXPORT ====================
  
  const handleExportPlans = async () => {
    try {
      const data = await apiCall('/api/subscription-plans/export/json');
      
      // Create downloadable file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscription_plans_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: 'Export Successful',
        description: `Exported ${data.total_plans} plans and ${data.total_addons} add-ons`
      });
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to export plans',
        variant: 'destructive'
      });
    }
  };
  
  const handleImportPlans = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate JSON structure
      let plansToImport = [];
      if (Array.isArray(data)) {
        plansToImport = data;
      } else if (data.plans && Array.isArray(data.plans)) {
        plansToImport = data.plans;
      } else {
        throw new Error('Invalid JSON format. Expected an array of plans or an object with "plans" array.');
      }
      
      if (plansToImport.length === 0) {
        throw new Error('No plans found in the file');
      }
      
      // Send to backend
      const result = await apiCall('/api/subscription-plans/import/json', {
        method: 'POST',
        body: JSON.stringify({
          plans: plansToImport,
          merge_strategy: 'upsert'
        })
      });
      
      toast({
        title: 'Import Successful',
        description: result.message
      });
      
      // Refresh plans list
      fetchData();
      
    } catch (error) {
      toast({
        title: 'Import Failed',
        description: error.message || 'Failed to import plans',
        variant: 'destructive'
      });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleEditChange = (planId, field, value) => {
    setEditData(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        [field]: value
      }
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    let successCount = 0;
    let errorCount = 0;

    // All editable fields - includes pricing fields AND feature fields
    const ALL_EDITABLE_FIELDS = [
      'monthly_price', 'annual_price', 'commission_percentage', 'trial_period_days',
      'razorpay_plan_id',
      ...planFeatures.map(f => f.key)
    ];

    for (const planId of Object.keys(editData)) {
      const plan = editData[planId];
      const originalPlan = plans.find(p => p.id === planId);
      
      if (!originalPlan) continue;
      
      // Build update payload with ALL editable fields
      const updatePayload = {};
      ALL_EDITABLE_FIELDS.forEach(field => {
        const newValue = plan[field];
        const oldValue = originalPlan[field];
        
        // Special handling for booleans - always include if explicitly set
        if (typeof newValue === 'boolean') {
          if (newValue !== oldValue) {
            updatePayload[field] = newValue;
          }
        }
        // Special handling for arrays (like support_level)
        else if (Array.isArray(newValue)) {
          const oldArr = Array.isArray(oldValue) ? oldValue : [];
          if (JSON.stringify(newValue.sort()) !== JSON.stringify(oldArr.sort())) {
            updatePayload[field] = newValue;
          }
        }
        // Standard comparison for other types
        else if (newValue !== oldValue && newValue !== undefined) {
          updatePayload[field] = newValue;
        }
      });

      // Skip if no changes for this plan
      if (Object.keys(updatePayload).length === 0) continue;

      try {
        await apiCall(`/api/subscription-plans/${planId}`, {
          method: 'PUT',
          body: JSON.stringify(updatePayload)
        });
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Failed to update plan ${planId}:`, error);
      }
    }

    setSaving(false);
    setEditMode(false);
    
    if (successCount > 0) {
      toast({ title: 'Success', description: `Updated ${successCount} plan(s)` });
      fetchData();
    }
    if (errorCount > 0) {
      toast({ title: 'Warning', description: `Failed to update ${errorCount} plan(s)`, variant: 'destructive' });
    }
    if (successCount === 0 && errorCount === 0) {
      toast({ title: 'Info', description: 'No changes detected' });
    }
  };

  const handleCancelEdit = () => {
    // Reset edit data to original plans
    const initialEditData = {};
    plans.forEach(plan => {
      initialEditData[plan.id] = { ...plan };
    });
    setEditData(initialEditData);
    setEditMode(false);
  };

  const handlePublish = async (planId, shouldPublish) => {
    try {
      await apiCall(`/api/subscription-plans/${planId}/publish`, {
        method: 'POST',
        body: JSON.stringify({ publish: shouldPublish })
      });
      toast({
        title: 'Success',
        description: `Plan ${shouldPublish ? 'published' : 'unpublished'} successfully`
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleAddOnChange = (index, field, value) => {
    setAddOnsData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSaveAddOns = async () => {
    setSavingAddOns(true);
    try {
      await apiCall('/api/subscription-plans/addons', {
        method: 'PUT',
        body: JSON.stringify({ addons: addOnsData })
      });
      toast({ title: 'Success', description: 'Add-ons pricing updated' });
      setAddOns(addOnsData);
      setAddOnsEditMode(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update add-ons',
        variant: 'destructive'
      });
    } finally {
      setSavingAddOns(false);
    }
  };

  // ==================== PLAN CRUD HANDLERS ====================
  
  const handleCreatePlan = async () => {
    if (!newPlanData.plan_name || !newPlanData.plan_code) {
      toast({ title: 'Error', description: 'Plan name and code are required', variant: 'destructive' });
      return;
    }
    
    setCreatingPlan(true);
    try {
      await apiCall('/api/subscription-plans/', {
        method: 'POST',
        body: JSON.stringify(newPlanData)
      });
      toast({ title: 'Success', description: `Plan "${newPlanData.plan_name}" created successfully` });
      setShowAddPlanModal(false);
      setNewPlanData({
        plan_name: '',
        plan_code: '',
        description: '',
        monthly_price: 0,
        annual_price: 0,
        commission_percentage: 15,
        max_listings: 10,
        media_storage_mb: 500,
        team_members_included: 1,
        max_locations: 1,
        trial_period_days: 14,
        support_level: 'email',
        display_order: 0
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create plan',
        variant: 'destructive'
      });
    } finally {
      setCreatingPlan(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    
    setDeletingPlan(true);
    try {
      await apiCall(`/api/subscription-plans/${planToDelete.id}`, {
        method: 'DELETE'
      });
      toast({ title: 'Success', description: `Plan "${planToDelete.plan_name}" deleted successfully` });
      setShowDeletePlanModal(false);
      setPlanToDelete(null);
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete plan. Only draft plans with no subscribers can be deleted.',
        variant: 'destructive'
      });
    } finally {
      setDeletingPlan(false);
    }
  };

  const openDeletePlanModal = (plan) => {
    setPlanToDelete(plan);
    setShowDeletePlanModal(true);
  };

  // ==================== ADD-ON CRUD HANDLERS ====================
  
  const handleCreateAddon = async () => {
    if (!newAddonData.name || !newAddonData.key) {
      toast({ title: 'Error', description: 'Add-on name and key are required', variant: 'destructive' });
      return;
    }
    
    setCreatingAddon(true);
    try {
      // Create the new addon object
      const newAddon = {
        ...newAddonData,
        id: newAddonData.key,
        linked_feature: newAddonData.linked_feature || null,
        created_at: new Date().toISOString()
      };
      
      // Try to POST the new addon directly first
      try {
        await apiCall('/api/subscription-plans/addons/create', {
          method: 'POST',
          body: JSON.stringify(newAddon)
        });
      } catch {
        // Fallback: Add to existing list and PUT all
        const currentAddons = Array.isArray(addOnsData) ? addOnsData : [];
        const updatedAddons = [...currentAddons, newAddon];
        
        await apiCall('/api/subscription-plans/addons', {
          method: 'PUT',
          body: JSON.stringify({ addons: updatedAddons })
        });
      }
      
      toast({ title: 'Success', description: `Add-on "${newAddonData.name}" created successfully` });
      setShowAddAddonModal(false);
      setNewAddonData({
        name: '',
        key: '',
        price: 0,
        frequency: '/mo',
        is_active: true,
        linked_feature: ''
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create add-on',
        variant: 'destructive'
      });
    } finally {
      setCreatingAddon(false);
    }
  };

  const handleDeleteAddon = async () => {
    if (!addonToDelete) return;
    
    setDeletingAddon(true);
    try {
      // Remove the addon from the list and save
      const updatedAddons = addOnsData.filter(a => a.key !== addonToDelete.key);
      
      await apiCall('/api/subscription-plans/addons', {
        method: 'PUT',
        body: JSON.stringify({ addons: updatedAddons })
      });
      
      // Also delete from DB directly
      await apiCall(`/api/subscription-plans/addons/${addonToDelete.key}`, {
        method: 'DELETE'
      }).catch(() => {}); // Ignore if endpoint doesn't exist
      
      toast({ title: 'Success', description: `Add-on "${addonToDelete.name}" deleted successfully` });
      setShowDeleteAddonModal(false);
      setAddonToDelete(null);
      setAddOnsData(updatedAddons);
      setAddOns(updatedAddons);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete add-on',
        variant: 'destructive'
      });
    } finally {
      setDeletingAddon(false);
    }
  };

  const openDeleteAddonModal = (addon) => {
    setAddonToDelete(addon);
    setShowDeleteAddonModal(true);
  };

  // ==================== PLAN REORDERING HANDLERS ====================
  
  const handleMovePlan = async (planId, direction) => {
    const currentIndex = plans.findIndex(p => p.id === planId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Check bounds
    if (newIndex < 0 || newIndex >= plans.length) return;
    
    setReordering(true);
    
    try {
      // Swap the plans in local state
      const newPlans = [...plans];
      [newPlans[currentIndex], newPlans[newIndex]] = [newPlans[newIndex], newPlans[currentIndex]];
      
      // Update display_order for both plans
      const updates = newPlans.map((plan, index) => ({
        id: plan.id,
        display_order: index
      }));
      
      // Update in backend
      let successCount = 0;
      for (const update of updates) {
        try {
          await apiCall(`/api/subscription-plans/${update.id}`, {
            method: 'PUT',
            body: JSON.stringify({ display_order: update.display_order })
          });
          successCount++;
        } catch (err) {
          console.error(`Failed to update order for plan ${update.id}:`, err);
        }
      }
      
      // Update local state with new order
      const reorderedPlans = newPlans.map((plan, index) => ({
        ...plan,
        display_order: index
      }));
      setPlans(reorderedPlans);
      
      // Update edit data as well
      const newEditData = {};
      reorderedPlans.forEach(plan => {
        newEditData[plan.id] = { ...plan };
      });
      setEditData(newEditData);
      
      toast({ 
        title: 'Success', 
        description: 'Plan order updated successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update plan order',
        variant: 'destructive'
      });
      // Refetch to restore correct order
      fetchData();
    } finally {
      setReordering(false);
    }
  };

  // ==================== FEATURE CRUD HANDLERS ====================
  
  const handleAddFeature = async () => {
    if (!newFeatureData.key || !newFeatureData.label) {
      toast({ title: 'Error', description: 'Feature key and label are required', variant: 'destructive' });
      return;
    }
    
    // Check if key already exists
    if (planFeatures.some(f => f.key === newFeatureData.key)) {
      toast({ title: 'Error', description: 'Feature key already exists', variant: 'destructive' });
      return;
    }
    
    const newFeature = {
      key: newFeatureData.key,
      label: newFeatureData.label,
      type: newFeatureData.type,
      options: newFeatureData.type === 'select' || newFeatureData.type === 'multiselect' 
        ? newFeatureData.options.filter(o => o.trim()) 
        : [],
      editable: true
    };
    
    const updatedFeatures = [...planFeatures, newFeature];
    setPlanFeatures(updatedFeatures);
    await saveFeatureDefinitions(updatedFeatures);
    setShowAddFeatureModal(false);
    setNewFeatureData({ key: '', label: '', type: 'boolean', options: [] });
    toast({ title: 'Success', description: `Feature "${newFeature.label}" added` });
  };

  const handleEditFeature = async () => {
    if (!featureToEdit) return;
    
    const updatedFeatures = planFeatures.map(f => 
      f.key === featureToEdit.key ? { ...featureToEdit } : f
    );
    setPlanFeatures(updatedFeatures);
    await saveFeatureDefinitions(updatedFeatures);
    setShowEditFeatureModal(false);
    setFeatureToEdit(null);
    toast({ title: 'Success', description: 'Feature updated and saved' });
  };

  const handleDeleteFeature = async () => {
    if (!featureToDelete) return;
    
    const updatedFeatures = planFeatures.filter(f => f.key !== featureToDelete.key);
    setPlanFeatures(updatedFeatures);
    await saveFeatureDefinitions(updatedFeatures);
    setShowDeleteFeatureModal(false);
    setFeatureToDelete(null);
    toast({ title: 'Success', description: `Feature "${featureToDelete.label}" removed` });
  };
  
  // Save feature definitions to backend
  const saveFeatureDefinitions = async (features) => {
    try {
      await apiCall('/api/subscription-plans/feature-definitions', {
        method: 'PUT',
        body: JSON.stringify(features)
      });
    } catch (error) {
      console.error('Failed to save feature definitions:', error);
      toast({ 
        title: 'Warning', 
        description: 'Feature changes saved locally but failed to persist to server',
        variant: 'destructive'
      });
    }
  };

  const getPlanIcon = (planCode) => {
    const icons = {
      'basic': <Package className="w-5 h-5" />,
      'free': <Package className="w-5 h-5" />,
      'pro': <Zap className="w-5 h-5" />,
      'professional': <Zap className="w-5 h-5" />,
      'business': <Crown className="w-5 h-5" />,
      'enterprise': <Crown className="w-5 h-5" />
    };
    return icons[planCode?.toLowerCase()] || <Package className="w-5 h-5" />;
  };

  const getPlanColor = (planCode) => {
    const colors = {
      'basic': 'bg-blue-500',
      'free': 'bg-blue-500',
      'pro': 'bg-purple-600',
      'professional': 'bg-purple-600',
      'business': 'bg-amber-500',
      'enterprise': 'bg-gradient-to-r from-amber-500 to-rose-500'
    };
    return colors[planCode?.toLowerCase()] || 'bg-slate-500';
  };

  const renderValue = (plan, feature) => {
    const value = editMode ? editData[plan.id]?.[feature.key] : plan[feature.key];
    
    if (editMode) {
      return renderEditableField(plan.id, feature, value);
    }
    
    return renderDisplayValue(feature, value);
  };

  const renderDisplayValue = (feature, value) => {
    switch (feature.type) {
      case 'boolean':
        return value ? (
          <Check className="w-5 h-5 text-green-600 mx-auto" />
        ) : (
          <X className="w-5 h-5 text-slate-300 mx-auto" />
        );
      case 'percentage':
        return <span className="font-medium">{value || 0}%</span>;
      case 'currency':
        if (value === -1) return <span className="text-slate-500">Custom</span>;
        return <span className="font-medium">₹{(value || 0).toLocaleString('en-IN')}</span>;
      case 'number':
        if (value === -1) return <span className="text-slate-500">Custom</span>;
        if (value === 0 || value >= 9999) return <span className="text-green-600 font-medium">Unlimited</span>;
        return <span className="font-medium">{value || 0}</span>;
      case 'select':
        const displayMap = {
          'none': <X className="w-5 h-5 text-slate-300 mx-auto" />,
          'included': <Check className="w-5 h-5 text-green-600 mx-auto" />,
          'add_on': <Badge variant="outline" className="text-xs">Add-on</Badge>,
          'read_only': <Badge className="bg-blue-100 text-blue-700 text-xs">Read Only</Badge>,
          'full': <Check className="w-5 h-5 text-green-600 mx-auto" />,
          'email': <span className="text-sm">Email</span>,
          'email_chat': <span className="text-sm">Email + Chat</span>,
          'priority': <Badge className="bg-purple-100 text-purple-700 text-xs">Priority</Badge>,
          'dedicated': <Badge className="bg-amber-100 text-amber-700 text-xs">Dedicated</Badge>
        };
        return displayMap[value] || <span className="text-sm">{value || '-'}</span>;
      case 'multiselect':
        // Handle array of support types
        const supportArr = Array.isArray(value) ? value : (value ? [value] : []);
        if (supportArr.length === 0) return <span className="text-slate-400">-</span>;
        const supportLabels = {
          'email': 'Email',
          'chat': 'Chat', 
          'call': 'Call',
          'priority': 'Priority',
          'dedicated': 'Dedicated'
        };
        return (
          <div className="flex flex-wrap gap-1 justify-center">
            {supportArr.map(s => (
              <Badge key={s} variant="outline" className="text-[10px] capitalize">
                {supportLabels[s] || s}
              </Badge>
            ))}
          </div>
        );
      default:
        return <span>{value || '-'}</span>;
    }
  };

  const renderEditableField = (planId, feature, value) => {
    const fieldId = `${feature.key}_${planId}`;
    
    switch (feature.type) {
      case 'boolean':
        return (
          <div className="flex justify-center">
            <input
              type="checkbox"
              id={fieldId}
              name={fieldId}
              checked={value === true}
              onChange={(e) => handleEditChange(planId, feature.key, e.target.checked)}
              className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500 cursor-pointer"
            />
          </div>
        );
      case 'percentage':
        return (
          <div className="flex items-center justify-center gap-1">
            <Input
              id={fieldId}
              type="number"
              className="w-16 h-8 text-center text-sm"
              value={value || 0}
              onChange={(e) => handleEditChange(planId, feature.key, parseFloat(e.target.value) || 0)}
            />
            <span className="text-slate-400 text-sm">%</span>
          </div>
        );
      case 'currency':
        return (
          <div className="flex items-center justify-center gap-1">
            <span className="text-slate-400 text-sm">₹</span>
            <Input
              id={fieldId}
              type="number"
              className="w-20 h-8 text-center text-sm"
              value={value || 0}
              onChange={(e) => handleEditChange(planId, feature.key, parseFloat(e.target.value) || 0)}
            />
          </div>
        );
      case 'number':
        return (
          <Input
            id={fieldId}
            type="number"
            className="w-20 h-8 text-center text-sm mx-auto"
            value={value || 0}
            onChange={(e) => handleEditChange(planId, feature.key, parseInt(e.target.value) || 0)}
          />
        );
      case 'select':
        return (
          <select
            id={fieldId}
            className="w-24 h-8 text-xs border rounded px-1 mx-auto block"
            value={value || feature.options[0]}
            onChange={(e) => handleEditChange(planId, feature.key, e.target.value)}
          >
            {feature.options.map(opt => (
              <option key={opt} value={opt}>{opt.replace('_', ' ')}</option>
            ))}
          </select>
        );
      case 'multiselect':
        // Multiselect with checkboxes for support_level
        const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
        const supportOptLabels = {
          'email': 'Email',
          'chat': 'Chat',
          'call': 'Call',
          'priority': 'Priority',
          'dedicated': 'Dedicated'
        };
        return (
          <div className="flex flex-col gap-1 items-start text-left min-w-[100px]">
            {feature.options.map(opt => {
              const checkboxId = `${fieldId}_${opt}`;
              return (
                <label key={opt} htmlFor={checkboxId} className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    id={checkboxId}
                    name={checkboxId}
                    checked={currentValues.includes(opt)}
                    onChange={(e) => {
                      const newValues = e.target.checked
                        ? [...currentValues, opt]
                        : currentValues.filter(v => v !== opt);
                      handleEditChange(planId, feature.key, newValues);
                    }}
                    className="w-3 h-3 rounded"
                  />
                  <span className="capitalize">{supportOptLabels[opt] || opt}</span>
                </label>
              );
            })}
          </div>
        );
      default:
        return (
          <Input
            id={fieldId}
            type="text"
            className="w-24 h-8 text-center text-sm mx-auto"
            value={value || ''}
            onChange={(e) => handleEditChange(planId, feature.key, e.target.value)}
          />
        );
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-full animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Subscription Plan Management</h1>
            <p className="text-slate-500">Configure subscription tiers, pricing, and add-ons</p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Import/Export Buttons */}
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportPlans}
                className="hidden"
                data-testid="import-plans-file-input"
              />
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                data-testid="import-plans-btn"
              >
                {importing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileUp className="h-4 w-4 mr-2" />
                )}
                Import Plans
              </Button>
              <Button 
                variant="outline" 
                onClick={handleExportPlans}
                data-testid="export-plans-btn"
              >
                <FileDown className="h-4 w-4 mr-2" />
                Export Plans
              </Button>
            </div>
            
            {/* Tax Context Toggle */}
            <div className="flex items-center gap-4 bg-slate-100 p-2 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Globe className="h-4 w-4" />
                Tax Preview:
              </div>
              <div className="flex bg-white rounded-md shadow-sm">
                <button
                  onClick={() => setTaxContext('india')}
                  className={`px-3 py-1.5 text-sm rounded-l-md transition-all ${
                    taxContext === 'india' 
                      ? 'bg-orange-500 text-white font-medium' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  🇮🇳 India (GST 18%)
                </button>
                <button
                  onClick={() => setTaxContext('international')}
                  className={`px-3 py-1.5 text-sm rounded-r-md transition-all ${
                    taxContext === 'international' 
                      ? 'bg-blue-500 text-white font-medium' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  🌍 International (0%)
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-900">GST @18% applies to all subscription fees</p>
            <p className="text-blue-700">Use the Edit button to modify plan features. Publish/Unpublish at the bottom of each column.</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="plans" className="gap-2" data-testid="plans-tab">
              <Package className="h-4 w-4" /> Subscription Plans
            </TabsTrigger>
            <TabsTrigger value="addons" className="gap-2" data-testid="addons-tab">
              <Plus className="h-4 w-4" /> Add-Ons Pricing
            </TabsTrigger>
            <TabsTrigger value="custom" className="gap-2" data-testid="custom-plans-tab">
              <Building2 className="h-4 w-4" /> Custom Plans
            </TabsTrigger>
            <TabsTrigger value="trial" className="gap-2" data-testid="trial-settings-tab">
              <Clock className="h-4 w-4" /> Trial Settings
            </TabsTrigger>
          </TabsList>

          {/* Plans Tab - Transposed Layout */}
          <TabsContent value="plans">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg">Plan Features Configuration</CardTitle>
                <div className="flex gap-2">
                  {editMode ? (
                    <>
                      <Button variant="outline" onClick={handleCancelEdit}>
                        <X className="w-4 h-4 mr-2" /> Cancel
                      </Button>
                      <Button onClick={handleSaveAll} disabled={saving} className="bg-green-600 hover:bg-green-700">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save All Changes
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => setShowAddFeatureModal(true)} className="border-amber-500 text-amber-600 hover:bg-amber-50">
                        <Plus className="w-4 h-4 mr-2" /> Add Feature
                      </Button>
                      <Button variant="outline" onClick={() => setShowAddPlanModal(true)} className="border-green-500 text-green-600 hover:bg-green-50">
                        <Plus className="w-4 h-4 mr-2" /> Add Plan
                      </Button>
                      <Button onClick={() => setEditMode(true)} className="bg-indigo-600 hover:bg-indigo-700">
                        <Edit2 className="w-4 h-4 mr-2" /> Edit Plans
                      </Button>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {editMode && (
                  <div className="bg-indigo-50 border-b border-indigo-200 p-3 flex items-center gap-2">
                    <Edit2 className="h-4 w-4 text-indigo-600" />
                    <span className="text-sm text-indigo-800 font-medium">Edit Mode Active - Modify values and click Save All Changes</span>
                  </div>
                )}
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    {/* Header Row - Plan Names */}
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left p-4 font-semibold text-slate-600 min-w-[200px] sticky left-0 bg-slate-50 z-10">
                          Feature
                        </th>
                        {plans.map((plan, index) => (
                          <th key={plan.id} className="p-4 text-center min-w-[150px]">
                            <div className="flex flex-col items-center gap-2">
                              {/* Reorder Arrows */}
                              <div className="flex items-center gap-1 mb-1">
                                <button
                                  onClick={() => handleMovePlan(plan.id, 'up')}
                                  disabled={index === 0 || reordering || editMode}
                                  className={`p-1 rounded hover:bg-slate-200 transition-colors ${
                                    index === 0 || reordering || editMode ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'
                                  }`}
                                  title="Move left"
                                >
                                  <ChevronUp className="w-4 h-4 text-slate-500 rotate-[-90deg]" />
                                </button>
                                <GripVertical className="w-4 h-4 text-slate-300" />
                                <button
                                  onClick={() => handleMovePlan(plan.id, 'down')}
                                  disabled={index === plans.length - 1 || reordering || editMode}
                                  className={`p-1 rounded hover:bg-slate-200 transition-colors ${
                                    index === plans.length - 1 || reordering || editMode ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'
                                  }`}
                                  title="Move right"
                                >
                                  <ChevronDown className="w-4 h-4 text-slate-500 rotate-[-90deg]" />
                                </button>
                              </div>
                              <div className={`w-10 h-10 rounded-xl ${getPlanColor(plan.plan_code)} text-white flex items-center justify-center shadow-sm`}>
                                {getPlanIcon(plan.plan_code)}
                              </div>
                              <span className="font-bold text-slate-800">{plan.plan_name}</span>
                              <div className="flex gap-1 flex-wrap justify-center">
                                <Badge variant="outline" className="text-[9px] bg-slate-100">
                                  #{index + 1}
                                </Badge>
                                {plan.is_featured && (
                                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">Popular</Badge>
                                )}
                                <Badge variant={plan.status === 'published' ? 'default' : 'outline'} 
                                  className={`text-[10px] ${plan.status === 'published' ? 'bg-green-100 text-green-700' : ''}`}>
                                  {plan.status}
                                </Badge>
                              </div>
                            </div>
                          </th>
                        ))}
                      </tr>
                      {/* Monthly Price Row - Editable */}
                      <tr className="bg-slate-100 border-b">
                        <td className="p-3 font-semibold text-slate-700 sticky left-0 bg-slate-100 z-10">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-slate-500" />
                            <span>Monthly Price (₹)</span>
                          </div>
                        </td>
                        {plans.map(plan => (
                          <td key={plan.id} className="p-3 text-center">
                            {editMode ? (
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-slate-400">₹</span>
                                <Input
                                  id={`monthly_price_${plan.id}`}
                                  name={`monthly_price_${plan.id}`}
                                  type="number"
                                  className="w-24 h-8 text-center font-bold"
                                  value={editData[plan.id]?.monthly_price ?? plan.monthly_price}
                                  onChange={(e) => handleEditChange(plan.id, 'monthly_price', parseFloat(e.target.value) || 0)}
                                />
                              </div>
                            ) : (
                              <div className="text-xl font-bold text-slate-900">
                                {plan.monthly_price === -1 ? 'Custom' : plan.monthly_price === 0 ? 'Free' : `₹${(plan.monthly_price || 0).toLocaleString('en-IN')}`}
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>
                      {/* Annual Price Row - Editable */}
                      <tr className="bg-slate-50 border-b">
                        <td className="p-3 font-semibold text-slate-700 sticky left-0 bg-slate-50 z-10">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-green-500" />
                            <span>Annual Price (₹)</span>
                          </div>
                        </td>
                        {plans.map(plan => (
                          <td key={plan.id} className="p-3 text-center">
                            {editMode ? (
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-slate-400">₹</span>
                                <Input
                                  id={`annual_price_${plan.id}`}
                                  name={`annual_price_${plan.id}`}
                                  type="number"
                                  className="w-24 h-8 text-center font-bold"
                                  value={editData[plan.id]?.annual_price ?? plan.annual_price}
                                  onChange={(e) => handleEditChange(plan.id, 'annual_price', parseFloat(e.target.value) || 0)}
                                />
                              </div>
                            ) : (
                              <div className="text-lg font-bold text-green-700">
                                {plan.annual_price === -1 ? 'Custom' : plan.annual_price === 0 || plan.annual_price === null ? 'Free' : `₹${(plan.annual_price || 0).toLocaleString('en-IN')}`}
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>
                    </thead>
                    
                    {/* Feature Rows */}
                    <tbody>
                      {planFeatures.map((feature, idx) => (
                        <tr key={feature.key} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-blue-50/30 transition-colors group`}>
                          <td className={`p-3 text-sm font-medium text-slate-700 sticky left-0 z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                            <div className="flex items-center justify-between">
                              <span>{feature.label}</span>
                              {!editMode && (
                                <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                                  <button
                                    onClick={() => {
                                      setFeatureToEdit(feature);
                                      setShowEditFeatureModal(true);
                                    }}
                                    className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600"
                                    title="Edit feature"
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setFeatureToDelete(feature);
                                      setShowDeleteFeatureModal(true);
                                    }}
                                    className="p-1 hover:bg-red-100 rounded text-slate-400 hover:text-red-600"
                                    title="Delete feature"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                          {plans.map(plan => (
                            <td key={plan.id} className="p-3 text-center">
                              {renderValue(plan, feature)}
                            </td>
                          ))}
                        </tr>
                      ))}
                      
                      {/* Razorpay Plan ID Row - Editable (admin-only repoint) */}
                      <tr className="bg-indigo-50/50 border-t-2 border-indigo-200">
                        <td className="p-3 font-semibold text-slate-700 sticky left-0 bg-indigo-50/50 z-10">
                          <div className="flex items-center gap-2">
                            <Link2 className="w-4 h-4 text-indigo-500" />
                            <span>Razorpay Plan ID</span>
                          </div>
                          <div className="text-[11px] text-slate-400 font-normal mt-1 max-w-[180px]">
                            Monthly billing only. Repointing only affects new sign-ups.
                          </div>
                        </td>
                        {plans.map(plan => {
                          const currentValue = editData[plan.id]?.razorpay_plan_id ?? plan.razorpay_plan_id ?? '';
                          return (
                            <td key={plan.id} className="p-3 text-center" data-testid={`razorpay-plan-id-cell-${plan.plan_code}`}>
                              {editMode ? (
                                <Input
                                  id={`razorpay_plan_id_${plan.id}`}
                                  name={`razorpay_plan_id_${plan.id}`}
                                  type="text"
                                  placeholder="plan_xxxxxxxxxxxx"
                                  className="w-40 h-8 text-center font-mono text-xs"
                                  value={currentValue}
                                  onChange={(e) => handleEditChange(plan.id, 'razorpay_plan_id', e.target.value.trim())}
                                  data-testid={`razorpay-plan-id-input-${plan.plan_code}`}
                                />
                              ) : currentValue ? (
                                <div className="flex items-center justify-center gap-1.5">
                                  <code className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded truncate max-w-[120px]" title={currentValue}>
                                    {currentValue}
                                  </code>
                                  <button
                                    onClick={() => handleCopyRazorpayPlanId(plan.id, currentValue)}
                                    className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600 transition-colors"
                                    title="Copy Razorpay Plan ID"
                                    data-testid={`razorpay-plan-id-copy-${plan.plan_code}`}
                                  >
                                    {copiedPlanId === plan.id ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400 italic">Not linked</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Publish/Unpublish Row */}
                      <tr className="bg-slate-100 border-t-2">
                        <td className="p-4 font-semibold text-slate-700 sticky left-0 bg-slate-100 z-10">
                          Status Actions
                        </td>
                        {plans.map(plan => (
                          <td key={plan.id} className="p-4 text-center">
                            <div className="flex flex-col gap-2 items-center">
                              {plan.status === 'published' ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handlePublish(plan.id, false)}
                                  className="text-amber-600 border-amber-300 hover:bg-amber-50 w-full"
                                >
                                  <EyeOff className="w-4 h-4 mr-1" /> Unpublish
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => handlePublish(plan.id, true)}
                                    className="bg-green-600 hover:bg-green-700 text-white w-full"
                                  >
                                    <Eye className="w-4 h-4 mr-1" /> Publish
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openDeletePlanModal(plan)}
                                    className="text-red-600 border-red-300 hover:bg-red-50 w-full"
                                  >
                                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Add-Ons Tab */}
          <TabsContent value="addons">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg">Add-Ons Pricing</CardTitle>
                <div className="flex gap-2">
                  {addOnsEditMode ? (
                    <>
                      <Button variant="outline" onClick={() => {
                        setAddOnsData([...addOns]);
                        setAddOnsEditMode(false);
                      }}>
                        <X className="w-4 h-4 mr-2" /> Cancel
                      </Button>
                      <Button onClick={handleSaveAddOns} disabled={savingAddOns} className="bg-green-600 hover:bg-green-700">
                        {savingAddOns ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save Pricing
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => setShowAddAddonModal(true)} className="border-green-500 text-green-600 hover:bg-green-50">
                        <Plus className="w-4 h-4 mr-2" /> Add Add-on
                      </Button>
                      <Button onClick={() => setAddOnsEditMode(true)} className="bg-indigo-600 hover:bg-indigo-700">
                        <Edit2 className="w-4 h-4 mr-2" /> Edit Pricing
                      </Button>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {addOnsEditMode && (
                  <div className="bg-indigo-50 border-b border-indigo-200 p-3 flex items-center gap-2">
                    <Edit2 className="h-4 w-4 text-indigo-600" />
                    <span className="text-sm text-indigo-800 font-medium">Edit Mode Active - Modify pricing and click Save Pricing</span>
                  </div>
                )}
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left p-4 font-semibold text-slate-600 w-8">#</th>
                        <th className="text-left p-4 font-semibold text-slate-600">Add-On Name</th>
                        <th className="text-center p-4 font-semibold text-slate-600 w-40">Price (₹)</th>
                        <th className="text-center p-4 font-semibold text-slate-600 w-32">Frequency</th>
                        <th className="text-center p-4 font-semibold text-slate-600 w-24">Active</th>
                        <th className="text-center p-4 font-semibold text-slate-600 w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(addOnsEditMode ? addOnsData : addOns).length > 0 ? (
                        (addOnsEditMode ? addOnsData : addOns).map((addon, idx) => (
                          <tr key={addon.key || idx} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-blue-50/30 transition-colors`}>
                            <td className="p-4 text-slate-500 text-sm">{idx + 1}</td>
                            <td className="p-4">
                              <span className="font-medium text-slate-800">{addon.name}</span>
                            </td>
                            <td className="p-4 text-center">
                              {addOnsEditMode ? (
                                <div className="flex items-center justify-center gap-1">
                                  <span className="text-slate-400">₹</span>
                                  <Input
                                    id={`addon_price_${idx}`}
                                    type="number"
                                    className="w-24 h-8 text-center"
                                    value={addon.price || 0}
                                    onChange={(e) => handleAddOnChange(idx, 'price', parseFloat(e.target.value) || 0)}
                                  />
                                </div>
                              ) : (
                                <span className="font-semibold text-slate-900">₹{(addon.price || 0).toLocaleString('en-IN')}</span>
                              )}
                            </td>
                            <td className="p-4 text-center">
                              {addOnsEditMode ? (
                                <select
                                  id={`addon_freq_${idx}`}
                                  className="h-8 text-sm border rounded px-2"
                                  value={addon.frequency || '/mo'}
                                  onChange={(e) => handleAddOnChange(idx, 'frequency', e.target.value)}
                                >
                                  <option value="/mo">Per Month</option>
                                  <option value="one-time">One-time</option>
                                  <option value="per-session">Per Session</option>
                                  <option value="/GB">Per GB</option>
                                </select>
                              ) : (
                                <Badge variant="outline" className="text-xs">
                                  {addon.frequency === '/mo' ? 'Per Month' : 
                                   addon.frequency === 'one-time' ? 'One-time' : 
                                   addon.frequency === 'per-session' ? 'Per Session' : addon.frequency}
                                </Badge>
                              )}
                            </td>
                            <td className="p-4 text-center">
                              {addOnsEditMode ? (
                                <Switch
                                  id={`addon_active_${idx}`}
                                  checked={addon.is_active !== false}
                                  onCheckedChange={(checked) => handleAddOnChange(idx, 'is_active', checked)}
                                />
                              ) : (
                                addon.is_active !== false ? (
                                  <Check className="w-5 h-5 text-green-600 mx-auto" />
                                ) : (
                                  <X className="w-5 h-5 text-slate-300 mx-auto" />
                                )
                              )}
                            </td>
                            <td className="p-4 text-center">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openDeleteAddonModal(addon)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        // Show default add-ons if none exist
                        ADD_ONS_LIST.map((addon, idx) => (
                          <tr key={addon.key} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                            <td className="p-4 text-slate-500 text-sm">{idx + 1}</td>
                            <td className="p-4">
                              <span className="font-medium text-slate-800">{addon.name}</span>
                            </td>
                            <td className="p-4 text-center">
                              <span className="font-semibold text-slate-900">₹{addon.defaultPrice.toLocaleString('en-IN')}</span>
                            </td>
                            <td className="p-4 text-center">
                              <Badge variant="outline" className="text-xs">
                                {addon.frequency === '/mo' ? 'Per Month' : 
                                 addon.frequency === 'one-time' ? 'One-time' : 
                                 addon.frequency === 'per-session' ? 'Per Session' : addon.frequency}
                              </Badge>
                            </td>
                            <td className="p-4 text-center">
                              <Check className="w-5 h-5 text-green-600 mx-auto" />
                            </td>
                            <td className="p-4 text-center">
                              <span className="text-slate-400 text-xs">Default</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Add-Ons Summary */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{ADD_ONS_LIST.length}</div>
                    <div className="text-sm text-slate-500">Total Add-Ons</div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      ₹{Math.min(...ADD_ONS_LIST.map(a => a.defaultPrice)).toLocaleString('en-IN')}
                    </div>
                    <div className="text-sm text-slate-500">Starting Price</div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Crown className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      ₹{Math.max(...ADD_ONS_LIST.map(a => a.defaultPrice)).toLocaleString('en-IN')}
                    </div>
                    <div className="text-sm text-slate-500">Premium Add-On</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Custom Plans Tab */}
          <TabsContent value="custom">
            <CustomPlansTab />
          </TabsContent>

          {/* Trial Settings Tab */}
          <TabsContent value="trial">
            <TrialSettingsTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* ==================== MODALS ==================== */}
      
      {/* Add Plan Modal */}
      <Dialog open={showAddPlanModal} onOpenChange={setShowAddPlanModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-600" />
              Create New Subscription Plan
            </DialogTitle>
            <DialogDescription>
              Fill in the details to create a new subscription plan. The plan will be created in draft status.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="plan_name">Plan Name *</Label>
              <Input
                id="plan_name"
                placeholder="e.g., Starter"
                value={newPlanData.plan_name}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, plan_name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan_code">Plan Code *</Label>
              <Input
                id="plan_code"
                placeholder="e.g., starter (lowercase, no spaces)"
                value={newPlanData.plan_code}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, plan_code: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Brief description of the plan"
                value={newPlanData.description}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="monthly_price">Monthly Price (₹)</Label>
              <Input
                id="monthly_price"
                type="number"
                value={newPlanData.monthly_price}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, monthly_price: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="annual_price">Annual Price (₹)</Label>
              <Input
                id="annual_price"
                type="number"
                value={newPlanData.annual_price}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, annual_price: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="commission_percentage">Commission (%)</Label>
              <Input
                id="commission_percentage"
                type="number"
                value={newPlanData.commission_percentage}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, commission_percentage: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_listings">Max Listings</Label>
              <Input
                id="max_listings"
                type="number"
                placeholder="-1 for custom, 0 for unlimited"
                value={newPlanData.max_listings}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, max_listings: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="media_storage_mb">Storage (MB)</Label>
              <Input
                id="media_storage_mb"
                type="number"
                value={newPlanData.media_storage_mb}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, media_storage_mb: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team_members_included">Team Members</Label>
              <Input
                id="team_members_included"
                type="number"
                value={newPlanData.team_members_included}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, team_members_included: parseInt(e.target.value) || 1 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_locations">Max Locations</Label>
              <Input
                id="max_locations"
                type="number"
                value={newPlanData.max_locations}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, max_locations: parseInt(e.target.value) || 1 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trial_period_days">Trial Days</Label>
              <Input
                id="trial_period_days"
                type="number"
                value={newPlanData.trial_period_days}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, trial_period_days: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={newPlanData.display_order}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="support_level">Support Level</Label>
              <select
                id="support_level"
                className="w-full h-10 border rounded-md px-3"
                value={newPlanData.support_level}
                onChange={(e) => setNewPlanData(prev => ({ ...prev, support_level: e.target.value }))}
              >
                <option value="email">Email</option>
                <option value="email_chat">Email + Chat</option>
                <option value="priority">Priority</option>
                <option value="dedicated">Dedicated</option>
              </select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPlanModal(false)}>Cancel</Button>
            <Button onClick={handleCreatePlan} disabled={creatingPlan} className="bg-green-600 hover:bg-green-700">
              {creatingPlan ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Plan Confirmation Modal */}
      <Dialog open={showDeletePlanModal} onOpenChange={setShowDeletePlanModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Subscription Plan
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the plan &quot;{planToDelete?.plan_name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Important Notes:</p>
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>Only draft plans can be deleted</li>
                  <li>Plans with active subscribers cannot be deleted</li>
                  <li>Consider deprecating published plans instead</li>
                </ul>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeletePlanModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeletePlan} disabled={deletingPlan}>
              {deletingPlan ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Add-on Modal */}
      <Dialog open={showAddAddonModal} onOpenChange={setShowAddAddonModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-600" />
              Create New Add-on
            </DialogTitle>
            <DialogDescription>
              Add a new add-on service that vendors can purchase.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="addon_name">Add-on Name *</Label>
              <Input
                id="addon_name"
                placeholder="e.g., Advanced Analytics"
                value={newAddonData.name}
                onChange={(e) => setNewAddonData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="addon_key">Add-on Key *</Label>
              <Input
                id="addon_key"
                placeholder="e.g., advanced_analytics (lowercase, underscores)"
                value={newAddonData.key}
                onChange={(e) => setNewAddonData(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="addon_price">Price (₹)</Label>
                <Input
                  id="addon_price"
                  name="addon_price"
                  type="number"
                  value={newAddonData.price}
                  onChange={(e) => setNewAddonData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="addon_frequency">Frequency</Label>
                <select
                  id="addon_frequency"
                  name="addon_frequency"
                  className="w-full h-10 border rounded-md px-3"
                  value={newAddonData.frequency}
                  onChange={(e) => setNewAddonData(prev => ({ ...prev, frequency: e.target.value }))}
                >
                  <option value="/mo">Per Month</option>
                  <option value="one-time">One-time</option>
                  <option value="per-session">Per Session</option>
                  <option value="/GB">Per GB</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="addon_linked_feature">Link to Feature (for Tier Limitations)</Label>
              <select
                id="addon_linked_feature"
                name="addon_linked_feature"
                className="w-full h-10 border rounded-md px-3"
                value={newAddonData.linked_feature}
                onChange={(e) => setNewAddonData(prev => ({ ...prev, linked_feature: e.target.value }))}
              >
                {LINKABLE_FEATURES.map(feat => (
                  <option key={feat.key} value={feat.key}>{feat.label}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500">
                Link this add-on to a core feature to apply tier limitations when vendors upgrade/downgrade.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="addon_is_active"
                name="addon_is_active"
                checked={newAddonData.is_active}
                onChange={(e) => setNewAddonData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="w-4 h-4 rounded"
              />
              <Label htmlFor="addon_is_active">Active by default</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAddonModal(false)}>Cancel</Button>
            <Button onClick={handleCreateAddon} disabled={creatingAddon} className="bg-green-600 hover:bg-green-700">
              {creatingAddon ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Add-on
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Add-on Confirmation Modal */}
      <Dialog open={showDeleteAddonModal} onOpenChange={setShowDeleteAddonModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Add-on
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the add-on &quot;{addonToDelete?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteAddonModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteAddon} disabled={deletingAddon}>
              {deletingAddon ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Add-on
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Feature Modal */}
      <Dialog open={showAddFeatureModal} onOpenChange={setShowAddFeatureModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-600" />
              Add New Feature Row
            </DialogTitle>
            <DialogDescription>
              Add a new feature to the plan comparison table.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="feature_label">Feature Label *</Label>
              <Input
                id="feature_label"
                placeholder="e.g., Custom Branding"
                value={newFeatureData.label}
                onChange={(e) => setNewFeatureData(prev => ({ ...prev, label: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="feature_key">Feature Key *</Label>
              <Input
                id="feature_key"
                placeholder="e.g., custom_branding (lowercase, underscores)"
                value={newFeatureData.key}
                onChange={(e) => setNewFeatureData(prev => ({ 
                  ...prev, 
                  key: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') 
                }))}
              />
              <p className="text-xs text-slate-500">This key will be used in the database</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="feature_type">Value Type</Label>
              <select
                id="feature_type"
                className="w-full h-10 border rounded-md px-3"
                value={newFeatureData.type}
                onChange={(e) => setNewFeatureData(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="boolean">Yes/No (Boolean)</option>
                <option value="number">Number</option>
                <option value="percentage">Percentage</option>
                <option value="currency">Currency (₹)</option>
                <option value="select">Single Select (Dropdown)</option>
                <option value="multiselect">Multi Select (Checkboxes)</option>
              </select>
            </div>
            {(newFeatureData.type === 'select' || newFeatureData.type === 'multiselect') && (
              <div className="space-y-2">
                <Label>Options (comma separated)</Label>
                <Input
                  placeholder="e.g., none, basic, premium"
                  value={newFeatureData.options.join(', ')}
                  onChange={(e) => setNewFeatureData(prev => ({ 
                    ...prev, 
                    options: e.target.value.split(',').map(o => o.trim().toLowerCase().replace(/\s+/g, '_')) 
                  }))}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddFeatureModal(false)}>Cancel</Button>
            <Button onClick={handleAddFeature} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Feature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Feature Modal */}
      <Dialog open={showEditFeatureModal} onOpenChange={setShowEditFeatureModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Edit Feature
            </DialogTitle>
            <DialogDescription>
              Update the feature label or type.
            </DialogDescription>
          </DialogHeader>
          
          {featureToEdit && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit_feature_label">Feature Label</Label>
                <Input
                  id="edit_feature_label"
                  value={featureToEdit.label}
                  onChange={(e) => setFeatureToEdit(prev => ({ ...prev, label: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Feature Key</Label>
                <Input
                  value={featureToEdit.key}
                  disabled
                  className="bg-slate-100"
                />
                <p className="text-xs text-slate-500">Key cannot be changed after creation</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_feature_type">Value Type</Label>
                <select
                  id="edit_feature_type"
                  className="w-full h-10 border rounded-md px-3"
                  value={featureToEdit.type}
                  onChange={(e) => setFeatureToEdit(prev => ({ ...prev, type: e.target.value }))}
                >
                  <option value="boolean">Yes/No (Boolean)</option>
                  <option value="number">Number</option>
                  <option value="percentage">Percentage</option>
                  <option value="currency">Currency (₹)</option>
                  <option value="select">Single Select (Dropdown)</option>
                  <option value="multiselect">Multi Select (Checkboxes)</option>
                </select>
              </div>
              {(featureToEdit.type === 'select' || featureToEdit.type === 'multiselect') && (
                <div className="space-y-2">
                  <Label>Options (comma separated)</Label>
                  <Input
                    value={(featureToEdit.options || []).join(', ')}
                    onChange={(e) => setFeatureToEdit(prev => ({ 
                      ...prev, 
                      options: e.target.value.split(',').map(o => o.trim().toLowerCase().replace(/\s+/g, '_')) 
                    }))}
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditFeatureModal(false)}>Cancel</Button>
            <Button onClick={handleEditFeature} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Feature Confirmation Modal */}
      <Dialog open={showDeleteFeatureModal} onOpenChange={setShowDeleteFeatureModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Feature
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove the feature &quot;{featureToDelete?.label}&quot; from the comparison table?
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p>This will remove the feature row from the UI. The data in plans will not be deleted.</p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteFeatureModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteFeature}>
              <Trash2 className="w-4 h-4 mr-2" />
              Remove Feature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
};

export default SubscriptionPlansManager;
